import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { LockIcon, SendIcon, SettingsIcon } from 'lucide-react';
import { AnnaAvatar } from '../components/avatar/AnnaAvatar';
import { HouseModeChip } from '../components/ui/HouseModeChip';
import { NoodstopButton } from '../components/ui/NoodstopButton';
import { NoodstopSheet } from '../components/ui/NoodstopSheet';
import { AgentMark } from '../components/ui/AgentMark';
import { PhoneProposal } from '../components/phone/PhoneProposal';
import { MomentControl } from '../components/wall/MomentControl';
import { useDb } from '../hooks/useDb';
import { db } from '../utils/db';
import { activeTasks, avatarThreads, nextEvent, pendingProposals } from '../utils/dbSelectors';
import { getAgent } from '../utils/agents';
import type { AvatarState } from '../types/anna';
const EASE = [0.23, 1, 0.32, 1] as const;

/** Telefoon: Anna onderweg. Wat wacht op jou eerst, dan je huis en wie er werkt. */
export function Telefoon() {
  const meta = useDb(t => t.meta);
  const pending = useDb(pendingProposals);
  const tasks = useDb(activeTasks);
  const threads = useDb(avatarThreads);
  const next = useDb(nextEvent);
  const rooms = useDb(t => t.rooms);
  const locks = useDb(t => t.devices.filter(d => d.kind === 'slot' || d.kind === 'alarm'));
  const [stopped, setStopped] = useState(false);
  const [question, setQuestion] = useState('');
  const [reply, setReply] = useState<string | null>(null);
  const [state, setState] = useState<AvatarState>('rust');
  const home = rooms.filter(r => r.presence !== 'leeg');
  const ask = (e: React.FormEvent) => {
    e.preventDefault();
    const q = question.trim();
    if (!q) return;
    setQuestion('');
    setState('denkt');
    setReply(null);
    window.setTimeout(() => {
      setState('spreekt');
      setReply(/deur|slot|alarm/i.test(q) ? 'Sloten en alarm bedien je zelf. Ze zijn nu op slot.' : `Genoteerd. Ik zet het klaar op je bureau; je ziet het in “Wacht op jou” als het je ok nodig heeft.`);
      window.setTimeout(() => setState('rust'), 2400);
    }, 900);
  };
  const stop = () => {
    setStopped(true);
    db.log({
      agent: 'systeem',
      summary: 'Noodstop via telefoon: alle agents gestopt',
      level: 'zelf',
      where: 'lokaal',
      outcome: 'gestopt'
    });
  };
  const resume = () => {
    setStopped(false);
    db.log({
      agent: 'systeem',
      summary: 'Anna hervat via telefoon',
      level: 'zelf',
      where: 'lokaal',
      outcome: 'uitgevoerd'
    });
  };
  return <div className="min-h-screen w-full bg-night-950 text-ink md:grid md:place-items-center md:py-16" data-id="element-1961" data-mp="3dx0pe6w62hsm">
      <div className="hidden md:block" data-id="element-1962" data-mp="39vp94rz6n8x3">
        <MomentControl index={0} count={1} title="Telefoon" onPrev={() => undefined} onNext={() => undefined} data-id="element-1963" data-mp="39unn5sgmp20l" />
      </div>

      {/* On desktop the phone sits in a device-sized frame; on a phone it is the screen. */}
      <div className="relative mx-auto flex min-h-screen w-full max-w-[430px] flex-col bg-night-950 md:h-[860px] md:min-h-0 md:overflow-hidden md:rounded-[48px] md:border md:border-white/10 md:bg-night-900" data-id="element-1964" data-mp="1yeyx2s82vmvk">
        <header className="flex items-center justify-between px-6 pt-6" data-id="element-1965" data-mp="hxqhttrg2x26">
          <time className="font-display text-[20px] font-light tabular-nums text-ink-soft" data-id="element-1966" data-mp="my4i2pmt12pn">{meta.time}</time>
          <div className="flex items-center gap-2" data-id="element-1967" data-mp="rr7ed5iyuq9j">
            <HouseModeChip mode={meta.mode} active data-id="element-1968" data-mp="wp8srd5g61eo" />
            <Link to="/beheer/logboek" aria-label="Beheer" className="grid h-10 w-10 place-items-center rounded-full text-ink-muted transition-colors duration-150 hover:bg-white/[0.06] hover:text-ink" data-id="element-1969" data-mp="wofxp9fup0cd">
              <SettingsIcon className="h-5 w-5" strokeWidth={1.75} data-id="element-1970" data-mp="1aoqpceoziiyl" />
            </Link>
          </div>
        </header>

        <main className="no-scrollbar flex-1 overflow-y-auto px-6 pb-40" data-id="element-1971" data-mp="37i26i5il03cu">
          <section aria-label="Anna" className="flex flex-col items-center pt-4 text-center" data-id="element-1972" data-mp="3cf22topmprqr">
            <div className="aspect-square w-[180px]" aria-hidden data-id="element-1973" data-mp="2h55ivuj3z954">
              <AnnaAvatar state={stopped ? 'gestopt' : state} threads={threads} data-id="element-1974" data-mp="2h7i2sd8fftkt" />
            </div>
            <AnimatePresence mode="wait" data-id="element-1975" data-mp="2c8ybdxb2dpl2">
              <motion.p key={reply ?? 'summary'} initial={{
              opacity: 0,
              y: 6
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0
            }} transition={{
              duration: 0.2,
              ease: EASE
            }} aria-live="polite" className="mt-2 max-w-[320px] font-display text-[22px] font-light leading-snug text-ink" data-id="element-1976" data-mp="2r44hsle2nqex">
                {reply ?? (pending.length === 0 ? 'Alles rustig. Niets wacht op je.' : `${pending.length} ${pending.length === 1 ? 'ding wacht' : 'dingen wachten'} op je.`)}
              </motion.p>
            </AnimatePresence>
            {next && !reply && <p className="mt-1 text-[14px] text-ink-muted" data-id="element-1977" data-mp="1apcqycwkqr7q">
                Volgende: <span className="tabular-nums text-ink-soft" data-id="element-1978" data-mp="3mbx0mxhd67xn">{next.start}</span> {next.title.split(',')[0]}
              </p>}
          </section>

          <section aria-labelledby="ph-waiting" className="mt-8" data-id="element-1979" data-mp="3cesfn0z98iif">
            <h2 id="ph-waiting" className="text-[13px] font-medium text-ink-muted" data-id="element-1980" data-mp="3cglmohds54qy">
              Wacht op jou
            </h2>
            {pending.length > 0 ? <ul className="mt-1 divide-y divide-white/[0.06]" data-id="element-1981" data-mp="8z3f8b1x5zr2">
                {pending.map(p => <PhoneProposal key={p.id} proposal={p} onApprove={db.approveProposal} onReject={db.rejectProposal} data-id="element-1982" data-mp="dw3m2o6lf6j8" />)}
              </ul> : <p className="mt-3 text-[14px] text-ink-soft" data-id="element-1983" data-mp="3yfprttc9qyy">Alles is beoordeeld.</p>}
          </section>

          <section aria-labelledby="ph-home" className="mt-8" data-id="element-1984" data-mp="2kncqaz6bchoi">
            <h2 id="ph-home" className="text-[13px] font-medium text-ink-muted" data-id="element-1985" data-mp="2op7v4dngdd71">
              Thuis
            </h2>
            <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-4 text-[14px]" data-id="element-1986" data-mp="v70h21oz5s1u">
              <div data-id="element-1987" data-mp="q7e1vmpl61qe">
                <dt className="text-ink-muted" data-id="element-1988" data-mp="q6umodrblrg2">Aanwezig</dt>
                <dd className="mt-0.5 text-ink" data-id="element-1989" data-mp="q9h1mygjj98b">{home.length ? home.map(r => r.name).join(', ') : 'Niemand'}</dd>
              </div>
              <div data-id="element-1990" data-mp="gi4ak92kgn3c">
                <dt className="text-ink-muted" data-id="element-1991" data-mp="ggjhyoztbe84">Binnen</dt>
                <dd className="mt-0.5 tabular-nums text-ink" data-id="element-1992" data-mp="cfzivfvoi0jc">
                  {(rooms.reduce((s, r) => s + r.temp, 0) / Math.max(1, rooms.length)).toFixed(1).replace('.', ',')} °C
                </dd>
              </div>
              {locks.map(d => <div key={d.id} data-id="element-1993" data-mp="2ibnqou5ur0n">
                  <dt className="flex items-center gap-1.5 text-ink-muted" data-id="element-1994" data-mp="2jmpyk7yjqpn">
                    <LockIcon className="h-3.5 w-3.5" aria-hidden data-id="element-1995" data-mp="3tpxkdxigpt80" /> {d.name}
                  </dt>
                  <dd className="mt-0.5 text-ink" data-id="element-1996" data-mp="1wv1hn6kj4081">{d.status}</dd>
                </div>)}
            </dl>
            <p className="mt-3 text-[12px] text-ink-muted" data-id="element-1997" data-mp="1rux1xirg39iv">Sloten en alarm bedien je zelf, in Home Assistant.</p>
          </section>

          <section aria-labelledby="ph-agents" className="mt-8" data-id="element-1998" data-mp="24eu2bxo6jp25">
            <h2 id="ph-agents" className="text-[13px] font-medium text-ink-muted" data-id="element-1999" data-mp="24czeoblixiud">
              Aan het werk
            </h2>
            <ul className="mt-3 space-y-3" data-id="element-2000" data-mp="2e5ommwhv8eky">
              {tasks.map(t => <li key={t.id} className="flex items-center gap-3" data-id="element-2001" data-mp="1hew65xr0skin">
                  <AgentMark agentId={t.agent} size="sm" data-id="element-2002" data-mp="1hgpav7i2ouo0" />
                  <span className="min-w-0 flex-1" data-id="element-2003" data-mp="1lfpu2r4gpzbj">
                    <span className="block truncate text-[14px] text-ink" data-id="element-2004" data-mp="1likfakvavo60">{t.title}</span>
                    <span className="block truncate text-[12px] text-ink-muted" data-id="element-2005" data-mp="1qhnj61zjnoce">
                      {stopped ? 'gestopt' : t.stepLabel}
                    </span>
                  </span>
                  <span className="h-1 w-14 shrink-0 overflow-hidden rounded-full bg-white/[0.08]" aria-label={`Stap ${t.step} van ${t.totalSteps}`} data-id="element-2006" data-mp="31uhlp1tsb2js">
                    <span className="block h-full rounded-full" style={{
                  width: `${t.step / t.totalSteps * 100}%`,
                  backgroundColor: stopped ? '#6B7480' : getAgent(t.agent).color
                }} data-id="element-2007" data-mp="3rxd4zash62ug" />
                  </span>
                </li>)}
            </ul>
          </section>
        </main>

        <div className="absolute inset-x-0 bottom-0 space-y-3 border-t border-white/[0.06] bg-night-950/95 px-4 pb-5 pt-3 backdrop-blur md:bg-night-900/95" data-id="element-2008" data-mp="ph3x9idsqxv">
          <form onSubmit={ask} className="flex items-center gap-2" data-id="element-2009" data-mp="agbs4jf6u0ly">
            <label htmlFor="ph-ask" className="sr-only" data-id="element-2010" data-mp="ogwbphsxg0gi">
              Vraag Anna iets
            </label>
            <input id="ph-ask" value={question} onChange={e => setQuestion(e.target.value)} placeholder="Vraag Anna iets" className="h-12 min-w-0 flex-1 rounded-full border border-white/10 bg-white/[0.04] px-5 text-[15px] text-ink placeholder:text-ink-muted focus:border-accent/50 focus:outline-none" data-id="element-2011" data-mp="jhjp8y5ywveg" />
            <button type="submit" aria-label="Verstuur" disabled={!question.trim()} className="grid h-12 w-12 shrink-0 place-items-center rounded-full bg-ink text-night-950 transition-opacity duration-150 disabled:opacity-30" data-id="element-2012" data-mp="15qgxzn33hiol">
              <SendIcon className="h-5 w-5" data-id="element-2013" data-mp="i0w5ach53724" />
            </button>
          </form>
          <div className="flex justify-center" data-id="element-2014" data-mp="i2z1xfcsryft">
            <NoodstopButton onTrigger={stop} data-id="element-2015" data-mp="n08zxje95if9" />
          </div>
        </div>
      </div>

      <NoodstopSheet open={stopped} onResume={resume} data-id="element-2016" data-mp="90psytx6gd4j" />
    </div>;
}